import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Plus, Search, Loader2 } from 'lucide-react';
import {
  practiceProductsService,
  type PracticeProduct,
} from '@/services/practiceProductsService';

interface PracticeProductPickerProps {
  clinicId: string;
  onSelect: (product: PracticeProduct) => void;
}

export default function PracticeProductPicker({ clinicId, onSelect }: PracticeProductPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState<PracticeProduct[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    practiceProductsService
      .list(clinicId, { search: search || undefined, activeOnly: true, limit: 20 })
      .then((result) => setProducts(result.products))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [clinicId, search, open]);

  const handleSelect = (product: PracticeProduct) => {
    onSelect(product);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="mr-1 h-3 w-3" />
          Add from Catalog
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products..."
              className="pl-9 h-9"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : products.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No products found
            </p>
          ) : (
            products.map((product) => (
              <button
                key={product.id}
                className="w-full text-left px-3 py-2 hover:bg-muted/50 border-b last:border-0 transition-colors"
                onClick={() => handleSelect(product)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{product.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {product.category}
                      {product.default_cpt_code ? ` | CPT: ${product.default_cpt_code}` : ''}
                    </p>
                  </div>
                  <span className="text-sm font-medium">
                    {product.price_cents ? `$${(product.price_cents / 100).toFixed(2)}` : 'Free'}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
